import { FC, Fragment, useEffect, useRef, useState, useContext } from 'react';
import { actions } from '../../../dispatch';
import { dispatch } from '../../../dispatch/dispatch';
import ContextStore from '../../../hooks/ContextStore';
import { IDeleteResponse } from '../../../interfaces/responses/DeleteResponse.interface';
import { IErrorResponse } from '../../../interfaces/responses/ErrorResponse.interface';

// ── ALL BUSINESS LOGIC UNCHANGED ──────────────────────────────────────────────
export const Logs: FC = () => {
  const { contextStore, setContextStore } = useContext(ContextStore);
  const bottomRef = useRef<null | HTMLDivElement>(null);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (!collapsed && bottomRef.current) {
      bottomRef.current.scrollTop = bottomRef.current.scrollHeight;
    }
  }, [contextStore.logs, collapsed]);

  const onClickClearLog = async () => {
    contextStore.setShowSpinner(true);
    const clearLogResponse: IDeleteResponse | IErrorResponse = await dispatch(
      actions.clearLog, {}, contextStore.token, { storeId: contextStore.store._id }
    );
    if ('error' in clearLogResponse) {
      contextStore.setShowSpinner(false);
      return console.log(clearLogResponse.error.message);
    }
    contextStore.setShowSpinner(false);
    setContextStore((past: any) => ({ ...past, logs: [] }));
  };

  // ── VISUAL REDESIGN — logic above unchanged ───────────────────────────────
  return (
    <div className="border-b border-border">
      {/* Header row */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-card">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2 w-2">
            {contextStore.logs.length > 0 && (
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            )}
            <span className={`relative inline-flex rounded-full h-2 w-2 ${contextStore.logs.length > 0 ? 'bg-emerald-500' : 'bg-muted-foreground/30'}`} />
          </span>
          <span className="text-xs font-semibold text-foreground uppercase tracking-wider">Logs</span>
          <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground tabular-nums">
            {contextStore.logs.length}
          </span>
        </div>

        <div className="flex items-center gap-1.5">
          {contextStore.store._id && (
            <button
              onClick={onClickClearLog}
              className="rounded-md border border-red-200 bg-red-50 px-2 py-1 text-[10px] font-semibold text-red-600 hover:bg-red-100 transition-colors dark:border-red-800 dark:bg-red-950/20 dark:text-red-400"
            >
              Clear
            </button>
          )}
          <button
            onClick={() => setCollapsed(c => !c)}
            className="rounded-md border border-border bg-background px-2 py-1 text-[10px] font-medium text-muted-foreground hover:bg-accent transition-colors"
          >
            {collapsed ? '▼' : '▲'}
          </button>
        </div>
      </div>

      {/* Terminal body */}
      {!collapsed && (
        <div
          ref={bottomRef}
          className="h-52 overflow-y-auto bg-gray-950 px-3 py-2 font-mono text-[11px] leading-relaxed"
          style={{ backgroundColor: '#0d1117' }}
        >
          {contextStore.logs.length === 0 ? (
            <div className="flex h-full items-center justify-center text-gray-500 text-xs">
              No logs — select a store to begin
            </div>
          ) : (
            contextStore.logs.map((log, index) => (
              <div key={index} className="mb-0.5" style={{
                color: (log as any).type === 'error' ? '#f87171'
                  : (log as any).type === 'update' ? '#4ade80'
                  : '#d1d5db'
              }}>
                <span style={{ color: '#4b5563', marginRight: '4px', userSelect: 'none' }}>›</span>
                {log.message}
                <span style={{ marginLeft: '8px', color: '#4b5563', fontSize: '9px' }}>
                  {new Date(log.date).toLocaleTimeString()}
                </span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};
